import { useState, useEffect } from 'react';
import axios from 'axios';
import { authService } from '../../services/authService';
import { usePermissions } from '../../hooks/usePermissions';

interface BankDetailsProps {
    razaoSocial: string;
    documentType: string;
    documentNumber: string;
    chavePix?: string;
    companyId: string;
    onSuccess?: (message: string) => void;
    onError?: (error: any) => void;
}

// Função para formatar CPF/CNPJ
const formatDocument = (document: string, type: string): string => {
    const cleanDoc = document.replace(/\D/g, '');

    if (type.toLowerCase() === 'cpf' && cleanDoc.length === 11) {
        return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    if (type.toLowerCase() === 'cnpj' && cleanDoc.length === 14) {
        return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return document;
};

export default function BankDetails({
    razaoSocial,
    documentType,
    documentNumber,
    chavePix = "",
    companyId,
    onSuccess,
    onError
}: BankDetailsProps) {
    const { hasPermission } = usePermissions();
    const [pixKey, setPixKey] = useState(chavePix);
    const [isLoading, setIsLoading] = useState(false);

    // Atualizar o pixKey quando a prop chavePix mudar
    useEffect(() => {
        setPixKey(chavePix);
    }, [chavePix]);

    const handleSavePixKey = async () => {
        if (!pixKey.trim()) {
            onError?.({ message: 'Por favor, digite uma chave PIX válida' });
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.put(
                `${import.meta.env.VITE_API_URL}/api/balance/pix-key/${companyId}`,
                { pixKey: pixKey.trim() },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.data.success) {
                onSuccess?.('Chave PIX salva com sucesso!');
            } else {
                onError?.({ message: response.data.message || 'Erro ao salvar chave PIX' });
            }
        } catch (error: any) {
            console.error('Erro ao salvar chave PIX:', error);
            onError?.(error);
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <div className="space-y-8">
            {/* SEÇÃO 1: Detalhes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Texto da seção 1 */}
                <div className="md:col-span-1">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white">Detalhes</h3>
                </div>

                {/* Card da seção 1 */}
                <div className="md:col-span-2">
                    <div className="bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
                        {/* Razão social */}
                        <div className="mb-5">
                            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                                Razão social
                            </label>
                            <div className="text-gray-800 dark:text-gray-200">
                                {razaoSocial}
                            </div>
                        </div>

                        {/* Documento */}
                        <div className="mb-5">
                            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                                Documento
                            </label>
                            <div className="flex items-center">
                                <span className="text-gray-800 dark:text-gray-200">{documentType.toUpperCase()} - {formatDocument(documentNumber, documentType)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SEÇÃO 2: Dados bancários */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Texto da seção 2 */}
                <div className="md:col-span-1">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white">Dados bancários</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Preencha com uma chave Pix da mesma titularidade do seu CPF / CNPJ
                    </p>
                </div>

                {/* Card da seção 2 */}
                <div className="md:col-span-2">
                    <div className="bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
                        <div>
                            <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                                Chave Pix
                            </label>
                            <input
                                type="text"
                                className="w-full rounded-lg border border-gray-300 bg-white py-3 px-4 mt-3 text-gray-800 outline-none focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-white/[0.05] dark:text-white"
                                value={pixKey}
                                onChange={(e) => setPixKey(e.target.value)}
                                placeholder="Digite sua chave PIX"
                            />
                        </div>

                        <div className="mt-4 flex items-center border-l-4 border-blue-500 bg-blue-50 p-4 dark:bg-blue-900/20">
                            <div className="mr-3 flex-shrink-0 text-blue-500">
                                <svg className="size-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <p className="text-sm text-blue-700 dark:text-blue-400">
                                Evite falhas no saque: a chave pix deve pertencer ao {documentType}: ({formatDocument(documentNumber, documentType)})
                            </p>
                        </div>

                        {hasPermission('financial.bank') && (
                            <div className="mt-6 flex justify-end">
                                <button
                                    className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={handleSavePixKey}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Salvando...' : 'Salvar alterações'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
